/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Dec 06 2024
 **/

import { create } from "zustand";

import { WhiteboardHook } from "../hooks/useWhiteboard";
import { MODE } from "../@drawing";

type WhiteboardState = {
	whiteboard: WhiteboardHook;
	whiteboardMode: WhiteboardMode;
	isOpen: boolean;
	userId: string;
	userName: string;
	penColor: string;
	toolState: TOOL_STATE;
	toolbarKey: number;
	isMouseDown: boolean;
	windowState: WindowState;
};

type WhiteboardAction = {
	setWhiteboard: (whiteboard: WhiteboardHook | undefined) => void;
	setWhiteboardMode: (whiteboardMode: WhiteboardMode) => void;
	setIsOpen: (isOpen: boolean) => void;
	setUserId: (userId: string) => void;
	setUserName: (userName: string) => void;
	setPenColor: (penColor: string) => void;
	setToolState: (toolState: TOOL_STATE) => void;
	resetToolbarKey: () => void;
	setIsMouseDown: (isMouseDown: boolean) => void;
	setWindowState: (windowState: WindowState) => void;
};

type WhiteboardStoreProps = WhiteboardState & WhiteboardAction;

const DefaultWhiteboardState: WhiteboardState = {
	whiteboard: undefined,
	whiteboardMode: "drawing",
	isOpen: false,
	userId: "",
	userName: "",
	penColor: "",
	toolState: MODE.CURSOR,
	toolbarKey: 0,
	isMouseDown: false,
	windowState: "normal"
};

const useWhiteboardStore = create<WhiteboardStoreProps>((set) => ({
	...DefaultWhiteboardState,
	setWhiteboard: (whiteboard) => set(() => ({ whiteboard })),
	setWhiteboardMode: (whiteboardMode) => set(() => ({ whiteboardMode })),
	setIsOpen: (isOpen) => set(() => ({ isOpen })),
	setUserId: (userId) => set(() => ({ userId })),
	setUserName: (userName) => set(() => ({ userName })),
	setPenColor: (penColor) => set(() => ({ penColor })),
	setToolState: (toolState) => set(() => ({ toolState })),
	resetToolbarKey: () => set(() => ({ toolbarKey: Date.now() })),
	setIsMouseDown: (isMouseDown) => set(() => ({ isMouseDown })),
	setWindowState: (windowState) => set(() => ({ windowState }))
}));

export default useWhiteboardStore;
